import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { Layout } from '../components/Layout';
import { studentsApi } from '../services/api';
import { Student, TestResult } from '../types';
import { useAuthStore } from '../store/authStore';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const StudentProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [student, setStudent] = useState<Student | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchStudentData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const scoresByCategory = useMemo(() => {
    const categoryScores: { [key: string]: number[] } = {};

    testResults.forEach(result => {
      const category = result.test?.category || 'Other';
      if (!categoryScores[category]) {
        categoryScores[category] = [];
      }
      // Ensure score is a number
      const score = Number(result.score);
      if (!isNaN(score)) {
        categoryScores[category].push(score);
      }
    });

    return Object.entries(categoryScores).map(([category, scores]) => {
      if (scores.length === 0) {
        return {
          category,
          average: 0,
          count: 0
        };
      }

      const sum = scores.reduce((total, score) => total + score, 0);
      const average = sum / scores.length;

      return {
        category,
        average,
        count: scores.length
      };
    });
  }, [testResults]);

  const overallAverage = useMemo(() => {
    if (testResults.length === 0) return 0;

    const validScores = testResults
      .map(result => Number(result.score))
      .filter(score => !isNaN(score));

    if (validScores.length === 0) return 0;

    const sum = validScores.reduce((total, score) => total + score, 0);
    return sum / validScores.length;
  }, [testResults]);

  // New data processing for comprehensive charts
  const allScoresChartData = useMemo(() => {
    if (testResults.length === 0) return null;

    // Sort test results by date
    const sortedResults = [...testResults]
      .filter(result => result.test?.date)
      .sort((a, b) => new Date(a.test!.date).getTime() - new Date(b.test!.date).getTime());

    return {
      labels: sortedResults.map(result => {
        const date = new Date(result.test!.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }),
      datasets: [
        {
          label: 'Điểm cá nhân',
          data: sortedResults.map(result => result.score),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        },
        {
          label: 'Điểm trung bình lớp',
          data: sortedResults.map(result => result.class_average || 0),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0.4,
          pointRadius: 4,
        },
      ],
    };
  }, [testResults]);

  const scoreDistributionData = useMemo(() => {
    if (testResults.length === 0) return null;

    const ranges = ['0-4.9', '5.0-5.9', '6.0-6.9', '7.0-7.9', '8.0-8.9', '9.0-10'];
    const distribution = ranges.map(range => {
      const [min, max] = range.split('-').map(Number);
      return testResults.filter(result => result.score >= min && result.score <= max).length;
    });

    return {
      labels: ranges.map(range => range),
      datasets: [
        {
          label: 'Số bài kiểm tra',
          data: distribution,
          backgroundColor: [
            'rgba(239, 68, 68, 0.6)',   // 0-49 (red)
            'rgba(251, 146, 60, 0.6)',  // 50-59 (orange)
            'rgba(234, 179, 8, 0.6)',   // 60-69 (yellow)
            'rgba(168, 85, 247, 0.6)',  // 70-79 (purple)
            'rgba(59, 130, 246, 0.6)',  // 80-89 (blue)
            'rgba(34, 197, 94, 0.6)',   // 90-100 (green)
          ],
          borderColor: [
            'rgb(239, 68, 68)',
            'rgb(251, 146, 60)',
            'rgb(234, 179, 8)',
            'rgb(168, 85, 247)',
            'rgb(59, 130, 246)',
            'rgb(34, 197, 94)',
          ],
          borderWidth: 2,
        },
      ],
    };
  }, [testResults]);

  const fetchStudentData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [studentResponse, resultsResponse] = await Promise.all([
        studentsApi.getById(parseInt(id)),
        studentsApi.getTestResults(parseInt(id))
      ]);

      setStudent(studentResponse.data);
      setTestResults(resultsResponse.data);
    } catch (_err) {
      setError('Failed to fetch student data');
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
      },
    },
  };

  const timelineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          title: function(context: { dataIndex: number }[]) {
            const resultIndex = context[0].dataIndex;
            const sortedResults = [...testResults]
              .filter(result => result.test?.date)
              .sort((a, b) => new Date(a.test!.date).getTime() - new Date(b.test!.date).getTime());
            return sortedResults[resultIndex]?.test?.name || '';
          },
          afterTitle: function(context: { dataIndex: number }[]) {
            const resultIndex = context[0].dataIndex;
            const sortedResults = [...testResults]
              .filter(result => result.test?.date)
              .sort((a, b) => new Date(a.test!.date).getTime() - new Date(b.test!.date).getTime());
            const result = sortedResults[resultIndex];
            return result?.test?.category || '';
          },
          footer: function(context: { dataIndex: number }[]) {
            const resultIndex = context[0].dataIndex;
            const sortedResults = [...testResults]
              .filter(result => result.test?.date)
              .sort((a, b) => new Date(a.test!.date).getTime() - new Date(b.test!.date).getTime());
            const result = sortedResults[resultIndex];
            return result?.class_rank ? `Hạng trong lớp: #${result.class_rank}` : '';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        ticks: {
          callback: function(value: string | number) {
            return value;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Thời gian'
        }
      }
    },
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-3 text-gray-500">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span>Loading student profile...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !student) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Student not found'}
          </div>
        </div>
      </Layout>
    );
  }

  const isValidNumber = (num: number) => !isNaN(num) && isFinite(num);

  const getScoreColor = (score: number) => {
    if (score >= 9) return 'text-emerald-600';
    if (score >= 8) return 'text-blue-600';
    if (score >= 7) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 9) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    if (score >= 8) return 'bg-blue-50 text-blue-700 border border-blue-200';
    if (score >= 7) return 'bg-amber-50 text-amber-700 border border-amber-200';
    return 'bg-red-50 text-red-700 border border-red-200';
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user?.role === 'admin' && (
          <div className="mb-6">
            <Link
              to="/students"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Students
            </Link>
          </div>
        )}

        {/* Student Hero Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="h-24 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
          <div className="px-8 pb-8">
            <div className="flex items-end space-x-5 -mt-12 mb-6">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white border-4 border-white shadow-md flex items-center justify-center flex-shrink-0">
                {student.profile_picture ? (
                  <img
                    src={`${student.profile_picture}`}
                    alt={`${student.name} profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<div class="text-gray-400 text-xs text-center">No Image</div>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                    <span className="text-3xl font-bold text-blue-400">
                      {student.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="pb-1">
                <h2 className="text-2xl font-bold text-gray-900">{student.name}</h2>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'SĐT', value: student.phone_number },
                { label: 'Ngày sinh', value: student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : null },
                { label: 'Lớp', value: student.class },
                { label: 'Trường', value: student.school },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3">
                  <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-800">{value || <span className="text-gray-400 font-normal">—</span>}</dd>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Số bài kiểm tra</h3>
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{testResults.length}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Điểm trung bình</h3>
              <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {isValidNumber(overallAverage) ? overallAverage.toFixed(1) : '0.0'}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Điểm tốt nhất</h3>
              <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {testResults.length > 0 ? Math.max(...testResults.map(r => r.score)).toFixed(1) : '—'}
            </p>
          </div>
        </div>

        {testResults.length > 0 ? (
          <>
            {/* All Test Scores Over Time Chart */}
            {allScoresChartData && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="mb-5">
                  <h3 className="text-base font-semibold text-gray-900">Tiến độ điểm số theo thời gian</h3>
                  <p className="text-sm text-gray-400 mt-0.5">Sự tiến bộ qua từng bài kiểm tra theo thứ tự thời gian</p>
                </div>
                <div className="h-96">
                  <Line data={allScoresChartData} options={timelineChartOptions} />
                </div>
              </div>
            )}

            {/* Score Distribution Chart */}
            {scoreDistributionData && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="mb-5">
                  <h3 className="text-base font-semibold text-gray-900">Phân bố điểm số</h3>
                  <p className="text-sm text-gray-400 mt-0.5">Thống kê số lượng bài kiểm tra theo từng khoảng điểm</p>
                </div>
                <div className="h-80">
                  <Bar
                    data={scoreDistributionData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { stepSize: 1 },
                          title: { display: true, text: 'Số bài kiểm tra' }
                        },
                        x: {
                          title: { display: true, text: 'Khoảng điểm' }
                        }
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Categories Progress Chart */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Theo kĩ năng</h3>
              {scoresByCategory.length > 0 ? (
                <div className="h-80">
                  <Bar
                    data={{
                      labels: scoresByCategory.map(cat => cat.category),
                      datasets: [
                        {
                          label: 'Điểm trung bình',
                          data: scoresByCategory.map(cat => cat.average),
                          backgroundColor: scoresByCategory.map(cat => {
                            const avg = cat.average;
                            if (avg >= 90) return 'rgba(34, 197, 94, 0.6)';
                            if (avg >= 80) return 'rgba(59, 130, 246, 0.6)';
                            if (avg >= 70) return 'rgba(234, 179, 8, 0.6)';
                            return 'rgba(239, 68, 68, 0.6)';
                          }),
                          borderColor: scoresByCategory.map(cat => {
                            const avg = cat.average;
                            if (avg >= 90) return 'rgb(34, 197, 94)';
                            if (avg >= 80) return 'rgb(59, 130, 246)';
                            if (avg >= 70) return 'rgb(234, 179, 8)';
                            return 'rgb(239, 68, 68)';
                          }),
                          borderWidth: 2,
                          borderRadius: 4,
                          borderSkipped: false,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            afterLabel: function(context) {
                              const categoryData = scoresByCategory[context.dataIndex];
                              return `Tests taken: ${categoryData.count}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 10,
                          ticks: {
                            callback: function(value) {
                              return value;
                            }
                          }
                        },
                        x: {
                          ticks: {
                            maxRotation: 45,
                            minRotation: 0
                          }
                        }
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="text-gray-500">Không có kết quả cho kĩ năng này</p>
              )}
            </div> */}

            {/* Score Trends by Category */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
              <div className="mb-5">
                <h3 className="text-base font-semibold text-gray-900">Kết quả theo kĩ năng</h3>
              </div>
              {scoresByCategory.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {scoresByCategory.map((categoryData) => {
                    const categoryResults = testResults
                      .filter(result => (result.test?.category || 'Other') === categoryData.category)
                      .sort((a, b) => new Date(a.test!.date).getTime() - new Date(b.test!.date).getTime());

                    const categoryChartData = {
                      labels: categoryResults.map(result => {
                        const testName = result.test?.name || 'Unknown Test';
                        return testName.length > 15 ? testName.substring(0, 15) + '...' : testName;
                      }),
                      datasets: [
                        {
                          label: `${categoryData.category} (Điểm cá nhân)`,
                          data: categoryResults.map(result => result.score),
                          borderColor: 'rgb(59, 130, 246)',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          borderWidth: 2,
                          fill: true,
                          tension: 0.4,
                        },
                        {
                          label: 'Trung bình cả lớp',
                          data: categoryResults.map(result => result.class_average || 0),
                          borderColor: 'rgb(239, 68, 68)',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          borderWidth: 2,
                          borderDash: [5, 5],
                          fill: false,
                          tension: 0.4,
                        },
                      ],
                    };

                    return (
                      <div key={categoryData.category} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-800">{categoryData.category}</h4>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-400">{categoryData.count} bài</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getScoreBadge(categoryData.average)}`}>
                              TB: {isValidNumber(categoryData.average) ? categoryData.average.toFixed(1) : '0.0'}
                            </span>
                          </div>
                        </div>
                        <Line data={categoryChartData} options={chartOptions} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No test data available for category trends.</p>
              )}
            </div>

            {/* Test Results Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Tất cả kết quả</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Ngày
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Bài kiểm tra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Kĩ năng
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Điểm
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Hạng
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        TB lớp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[...testResults]
                      .sort((a, b) => new Date(b.test?.date || 0).getTime() - new Date(a.test?.date || 0).getTime())
                      .map((result) => (
                      <tr key={result.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {result.test?.date ? new Date(result.test.date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-800">
                          {result.test?.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {result.test?.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-bold ${getScoreColor(result.score)}`}>
                            {result.score}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {result.class_rank ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                              #{result.class_rank}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.class_average !== undefined ? result.class_average : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-gray-400">No test results found for this student.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};
